/**
 * @fileoverview The two ways to add (D78, owner 2026-10-22: "the pop up should allow the gardener to upload a photo OR
 * type it in"): "Take or upload a photo" and "Type it in", as two rows, and the pop-up that holds them (AddChooser).
 *
 * The two rows look alike: the same frame, font and left alignment (owner, after mock-up revision 2: "the take or
 * upload should be same font and justification as type it in"). The photo row is first but not heavier. Each is an
 * icon in a small square, what it is in bold, and one muted line, 56 px at least, as Today's start doors are.
 *
 * The photo row's line is the owner's sentence ("Of plant(s) or of seed packet(s). Grow separates, names them and you
 * save them.") on a plan with the photo reading; a plan without it (Free, a paused plan) gets the line that promises
 * no names ("… You name them and save them."), shown until access is known so a Free gardener is never promised names
 * (aiKnownAllowed, D70 F4). The photo row works on Free too: nothing is read, the gardener names each photo (P6).
 *
 * Where: the Plants pop-up (useAddPlant: the add placeholders, the empty garden in place, Seasons' day one, ?add=1, a
 * bed's "Add a new plant here") and the Beds pop-up (useAddBed: the add placeholders, the empty Beds page).
 * API: none (useAiAccess reads the billing status the app already has).
 * Words (en/es): add.photo, add.type; the hints are the caller's (add.plant.*, add.bed.*).
 *
 * @module AddWays
 */

import React from 'react';
import { Camera, Plus } from 'lucide-react';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAiAccess } from '@/features/billing/lib/useAiAccess';
import { aiKnownAllowed } from '@/features/welcome/lib/startDoors';

/** The words under the two rows: each a locale key. */
export interface AddWaysWords {
  /** Under the photo row, on a plan with the photo reading. */
  photoHint: string;
  /** Under the photo row, on a plan without it. */
  photoHintNoAi: string;
  /** Under "Type it in". */
  typeHint: string;
}

/** What the rows are given. */
interface AddWaysProps extends AddWaysWords {
  /** "Take or upload a photo". */
  onPhoto: () => void;
  /** "Type it in". */
  onType: () => void;
}

/**
 * One way to add: an icon in a small square, what it is, and a line under it. Both rows are drawn by this, so they
 * cannot drift apart.
 *
 * @param props.icon - The icon.
 * @param props.title - What the way is.
 * @param props.hint - The line under it.
 * @param props.onClick - Starts it.
 */
const Way: React.FC<{ icon: React.ReactNode; title: string; hint: string; onClick: () => void }> = ({ icon, title, hint, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex min-h-[56px] w-full items-center gap-3 rounded-lg border bg-card p-3 text-left transition-colors hover:bg-muted"
  >
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-primary" aria-hidden="true">{icon}</span>
    <span className="min-w-0">
      <span className="block font-semibold leading-snug">{title}</span>
      <span className="block text-sm leading-snug text-muted-foreground">{hint}</span>
    </span>
  </button>
);

/**
 * The two rows: "Take or upload a photo", then "Type it in".
 *
 * @param props - AddWaysProps.
 */
export const AddWays: React.FC<AddWaysProps> = ({ photoHint, photoHintNoAi, typeHint, onPhoto, onType }) => {
  const { reactTranslate: t } = useTranslation();
  // A plan with no photo reading gets the line that promises no names; until access is known, that line (C77).
  const reads = aiKnownAllowed(useAiAccess());
  return (
    <div className="flex w-full flex-col gap-2">
      <Way icon={<Camera className="h-5 w-5" />} title={t('add.photo')} hint={t(reads ? photoHint : photoHintNoAi)} onClick={onPhoto} />
      <Way icon={<Plus className="h-5 w-5" />} title={t('add.type')} hint={t(typeHint)} onClick={onType} />
    </div>
  );
};

/** What the pop-up is given. */
interface AddChooserProps extends AddWaysProps {
  /** Shown. */
  open: boolean;
  /** Closed (×, a tap outside, Escape) or opened. */
  onOpenChange: (open: boolean) => void;
  /** "Add a plant" or "Add a bed". */
  title: string;
}

/**
 * The pop-up: a sheet from the bottom on a phone (BottomPanel), the two rows in it.
 *
 * @param props - AddChooserProps.
 */
export const AddChooser: React.FC<AddChooserProps> = ({ open, onOpenChange, title, ...ways }) => (
  <BottomPanel open={open} onOpenChange={onOpenChange} title={title}>
    <AddWays {...ways} />
  </BottomPanel>
);
